import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
    Box,
    Collapse,
    Grid,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { HostPayment } from '../../../app/Slices/SevenDayApiTypes';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import { CustomChip } from '../../../layout/styledcomponents/CustomChip';
import UserRole from '../../../shared/AuthRoles';
import { formatCurrencyShort, getPaymentStatus } from '../../../shared/utils';
import FormSwitch from '../../shared/FormSwitch';
import GenericTable, {
    LinkType,
    tableBodyCellsStyles,
    tableHeadCellStyles,
} from '../../shared/GenericTable';

export const Payment = ({
    payment,
    serviceFeePercentage,
}: {
    payment: HostPayment;
    serviceFeePercentage: number;
}) => {
    const loggedInUser = UserRole();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const navigate = useNavigate();

    const { control, setValue } = useForm<{
        projected: boolean;
    }>({
        defaultValues: {
            projected: false,
        },
    });
    const projected = useWatch({ control, name: 'projected' });
    const { year, month } = useParams();
    const [showProjected, setShowProjected] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const now = new Date();
        if (
            Number(year) === now.getFullYear() &&
            Number(month) === now.getMonth()
        ) {
            setShowProjected(true);
        }
        if (showProjected) {
            setSearchParams({
                projected: projected ? 'true' : 'false',
            });
        }
    }, [projected]);

    useEffect(() => {
        if (searchParams.get('projected') === 'true') {
            setValue('projected', true);
        }
    }, []);

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'moduleName',
            header: 'Experience',
        },
        {
            accessorKey: 'id',
            header: 'ID',
        },
        {
            accessorKey: 'ref',
            header: 'Booking Ref:',
        },
        {
            accessorKey: 'type',
            header: 'Charge Type',
            filterVariant: 'select',
            size: 200,
            mantineFilterSelectProps: {
                data: [
                    {
                        label: 'Additional Expense',
                        value: 'Additional Expense',
                    },
                    {
                        label: 'Reservation Charge',
                        value: 'Reservation Charge',
                    },
                ],
                clearable: true,
            },
            Cell: ({ row, cell }) => {
                const value = cell.getValue<string>();

                return (
                    <CustomChip
                        label={
                            value === 'Reservation Charge'
                                ? 'Reservation Charge'
                                : 'Additional Expense'
                        }
                        color={
                            value === 'Reservation Charge' ? 'green' : 'yellow'
                        }
                        variant='outlined'
                    />
                );
            },
        },
        {
            accessorKey: 'startDate',
            header: 'Check-in',
        },
        {
            accessorKey: 'endDate',
            header: 'Checkout',
        },

        {
            accessorKey: 'guestName',
            header: 'Guest',
            Cell: ({ row, cell }) =>
                cell.getValue() === undefined
                    ? 'Booked by owner'
                    : cell.getValue<string>(),
        },
        {
            accessorKey: 'bookedFor',
            header: 'For',
        },

        {
            accessorKey: 'amount',
            header: 'Amount',
            Cell: ({ cell }) => formatCurrencyShort(cell.getValue<number>()),
            filterFn: 'betweenInclusive',
            mantineTableHeadCellProps: {
                align: 'right',
                style: { ...tableHeadCellStyles, paddingRight: 40 },
            },
            mantineTableBodyCellProps: {
                align: 'right',
                style: { ...tableBodyCellsStyles, paddingRight: 40 },
            },
        },

        {
            accessorKey: 'refund',
            header: 'Refund',
            Cell: ({ cell }) =>
                formatCurrencyShort(cell.getValue<number>() ?? 0.0),
            filterFn: 'betweenInclusive',
            mantineTableHeadCellProps: {
                align: 'right',
                style: { ...tableHeadCellStyles, paddingRight: 40 },
            },
            mantineTableBodyCellProps: {
                align: 'right',
                style: { ...tableBodyCellsStyles, paddingRight: 40 },
            },
        },
    ];

    const rows = payment
        ? payment.bookings.map((booking) => {
              const {
                  id,
                  moduleName,
                  startDate,
                  endDate,
                  guestName,
                  bookedFor,
                  amount,
                  refund,
              } = booking;
              return {
                  id,
                  moduleName,
                  bookingId: id,
                  type: 'Reservation Charge',
                  startDate: moment(startDate).format('D MMM, YYYY'),
                  endDate: moment(endDate).format('D MMM, YYYY'),
                  guestName: guestName,
                  bookedFor,
                  amount,
                  refund,
                  ref: `#${id}`,
              };
          })
        : [];

    const expenseRows = payment
        ? payment?.expenses.map((expense) => {
              const {
                  id,
                  module,
                  name,
                  date,
                  owner,
                  amount,
                  guest,
                  bookingId,
              } = expense;
              return {
                  id,
                  bookingId,
                  moduleName: module?.name,
                  type: 'Additional Expense',
                  startDate: moment(date).format('D MMM, YYYY'),
                  endDate: 'N/A',
                  guestName: guest?.name,
                  bookedFor: name,
                  owner: owner?.name,
                  amount,
                  ref: `#${bookingId}`,
              };
          })
        : [];

    const data = [...rows, ...expenseRows].sort((a, b) => {
        const dateA = new Date(a.startDate || a.startDate).getTime();
        const dateB = new Date(b.startDate || b.startDate).getTime();
        return dateA - dateB; // Sorts in ascending order (oldest to newest)
    });

    const linkState = { name: 'hostPayment' };

    const processedData = useMemo(() => {
        if (!data) return [];
        return data;
    }, [data]);

    return (
        <Stack
            sx={{
                height: isMobile ? '100%' : '80%',
                width: '100%',
            }}
            spacing={2}
        >
            <Box>
                <Typography
                    variant='h1'
                    sx={{
                        textAlign: 'left',
                        textTransform: 'uppercase',
                    }}
                >
                    Payment Items
                </Typography>
            </Box>
            <Grid container rowSpacing={1} paddingTop={'30px'}>
                <Collapse in={searchParams.get('projected') === 'true'}>
                    {' '}
                    <Grid container item>
                        <Grid item>
                            <Typography
                                color='textPrimary'
                                sx={{
                                    fontSize: '12px',
                                    fontStyle: 'italic',
                                }}
                            >
                                Overview of potential future earnings based on
                                current approved reservations
                            </Typography>
                        </Grid>
                    </Grid>
                </Collapse>
                {loggedInUser.role === 'admin' && (
                    <Grid container item>
                        <Grid item xs={5} lg={2}>
                            <Typography color='textPrimary'>Owner:</Typography>
                        </Grid>
                        <Grid item xs={6} md={1.5} textAlign={'right'}>
                            <Typography color='textPrimary'>
                                {payment.ownerName}
                            </Typography>
                        </Grid>
                    </Grid>
                )}
                {payment.id && (
                    <Grid container item>
                        <Grid item xs={5} lg={2}>
                            <Typography color='textPrimary'>Ref:</Typography>
                        </Grid>
                        <Grid item xs={6} md={1.5} textAlign={'right'}>
                            <Typography color='textPrimary'>
                                #{payment.id}
                            </Typography>
                        </Grid>
                    </Grid>
                )}
                <Grid container item>
                    <Grid item xs={5} lg={2}>
                        <Typography color='textPrimary'>
                            Payment Period:
                        </Typography>
                    </Grid>
                    <Grid item xs={6} md={1.5} textAlign={'right'}>
                        <Typography color='textPrimary'>
                            {`${moment(payment.periodStart).format(
                                'D MMM',
                            )} - ${moment(payment.periodEnd).format(
                                'D MMM, YYYY',
                            )}`}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container item>
                    <Grid item xs={5} lg={2}>
                        <Typography color='textPrimary'>Status:</Typography>
                    </Grid>
                    <Grid item xs={6} md={1.5} textAlign={'right'}>
                        <Typography color='textPrimary'>
                            {getPaymentStatus(payment)}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container item>
                    <Grid item xs={5} lg={2}>
                        <Typography color='textPrimary'>
                            Reservation Total:
                        </Typography>
                    </Grid>
                    <Grid item xs={6} md={1.5} textAlign={'right'}>
                        <Typography color='textPrimary'>
                            {formatCurrencyShort(payment.bookingsTotal)}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container item>
                    <Grid item xs={6} md={5} lg={2}>
                        <Typography color='textPrimary'>
                            7DAY Service Fee ({serviceFeePercentage}%):
                        </Typography>
                    </Grid>
                    <Grid item xs={5} md={1.5} textAlign={'right'}>
                        <Typography color='textPrimary'>
                            - ({formatCurrencyShort(payment.serviceFeeAmount)})
                        </Typography>
                    </Grid>
                </Grid>
                {payment.billingExcludedBookingsTotal !== 0 && (
                    <Grid container item>
                        <Grid item xs={5} lg={2}>
                            <Typography color='textPrimary'>
                                Excluded Bookings Total
                            </Typography>
                        </Grid>
                        <Grid item xs={6} md={1.5} textAlign={'right'}>
                            <Typography color='textPrimary'>
                                - (
                                {formatCurrencyShort(
                                    payment.billingExcludedBookingsTotal,
                                )}
                                )
                            </Typography>
                        </Grid>
                    </Grid>
                )}
                {payment.guestServiceFeeAmount !== 0 && (
                    <Grid container item>
                        <Grid item xs={5} lg={2}>
                            <Typography color='textPrimary'>
                                Guest Service Fee Amount
                            </Typography>
                        </Grid>
                        <Grid item xs={6} md={1.5} textAlign={'right'}>
                            <Typography color='textPrimary'>
                                -(
                                {formatCurrencyShort(
                                    payment.guestServiceFeeAmount,
                                )}
                                )
                            </Typography>
                        </Grid>
                    </Grid>
                )}

                {payment.refundTotal !== 0 && (
                    <Grid container item>
                        <Grid item xs={6} md={5} lg={2}>
                            <Typography color='textPrimary'>
                                Refund Total :
                            </Typography>
                        </Grid>
                        <Grid item xs={5} md={1.5} textAlign={'right'}>
                            <Typography color='textPrimary'>
                                {formatCurrencyShort(payment.refundTotal)}
                            </Typography>
                        </Grid>
                    </Grid>
                )}
                {payment.expensesTotal !== 0 && (
                    <Grid container item>
                        <Grid item xs={5} lg={2}>
                            <Typography color='textPrimary'>
                                Expense Total:
                            </Typography>
                        </Grid>
                        <Grid item xs={6} md={1.5} textAlign={'right'}>
                            <Typography color='textPrimary'>
                                {formatCurrencyShort(payment.expensesTotal)}
                            </Typography>
                        </Grid>
                    </Grid>
                )}
                <Grid container item>
                    <Grid item xs={5} lg={2}>
                        <Typography color='textPrimary'>Gross:</Typography>
                    </Grid>
                    <Grid item xs={6} md={1.5} textAlign={'right'}>
                        <Typography color='textPrimary'>
                            {formatCurrencyShort(payment.amount)}
                        </Typography>
                    </Grid>
                </Grid>

                {showProjected && (
                    <Grid container>
                        <Grid item xs={5} lg={2}>
                            <FormSwitch
                                name='projected'
                                control={control}
                                label='Projected Earnings'
                            />
                        </Grid>
                    </Grid>
                )}
            </Grid>

            <Box paddingTop={'30px'}>
                <GenericTable
                    columns={columns}
                    data={processedData}
                    tableName={`Reservations-${moment(
                        payment.periodStart,
                    ).format('D MMM')} - ${moment(payment.periodEnd).format(
                        'D MMM, YYYY',
                    )}`}
                    loading={!processedData}
                    linkType={LinkType.Reservation}
                    linkState={linkState}
                />
            </Box>
            <Grid paddingTop={'30px'}>
                <CustomButton
                    onClick={() => navigate(-1)}
                    color='primary'
                    sx={{
                        marginLeft: !isMobile ? '-8px' : '-18px',
                        '&:hover': { background: 'none' },
                    }}
                >
                    <ArrowBackIcon />
                    {isMobile ? null : (
                        <Typography
                            sx={{
                                fontSize: '12px',
                                paddingLeft: '5px',
                            }}
                        >
                            Back
                        </Typography>
                    )}
                </CustomButton>
            </Grid>
        </Stack>
    );
};
